import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { Employee, Job } from '../../types/backend.types';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';
import { Clock, ChevronDown, LogIn, LogOut, AlertCircle, CheckCircle, Users } from 'lucide-react';
import { CustomTimePicker } from '../common/CustomTimePicker';

interface ClockedInEmployee {
  employee_id: number;
  first_name: string;
  last_name: string;
  nickname: string;
  job_id: number;
  job_name: string;
  job_number: string;
  clock_in: string;
  entry_id: number;
  minutes_clocked_in: number;
}

export const ManualTimeEntry: React.FC = () => {
  const auth = useContext(AuthContext);

  // Admin-only access control
  if (!auth?.user) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>Please log in to access this page.</p>
      </div>
    );
  }

  if (auth.user.role !== 'admin') {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>This page is only accessible to administrators.</p>
        <p>Your current role: <strong>{auth.user.role}</strong></p>
      </div>
    );
  }

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [clockedInEmployees, setClockedInEmployees] = useState<ClockedInEmployee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [showJobDropdown, setShowJobDropdown] = useState(false);
  const [jobSearchTerm, setJobSearchTerm] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadEmployees();
    loadJobs();
    loadClockedInEmployees();

    // Set default date to today (local timezone)
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    setDate(`${year}-${month}-${day}`);

    // Set default time to current time
    const hours = today.getHours().toString().padStart(2, '0');
    const minutes = today.getMinutes().toString().padStart(2, '0');
    setTime(`${hours}:${minutes}`);
  }, []);

  const loadEmployees = async () => {
    try {
      const response = await api.get(API_CONFIG.ENDPOINTS.EMPLOYEES);
      if (response.data.success) {
        setEmployees(response.data.data);
      }
    } catch (err) {
      console.error('Failed to load employees:', err);
    }
  };

  const loadJobs = async () => {
    try {
      const response = await api.get(API_CONFIG.ENDPOINTS.JOBS);
      if (response.data.success) {
        // Only show active jobs
        const activeJobs = response.data.data.filter((job: Job) => job.is_active);
        setJobs(activeJobs);
      }
    } catch (err) {
      console.error('Failed to load jobs:', err);
    }
  };

  const loadClockedInEmployees = async () => {
    try {
      const response = await api.get(API_CONFIG.ENDPOINTS.CLOCKED_IN_EMPLOYEES);
      if (response.data.success) {
        setClockedInEmployees(response.data.data);
      }
    } catch (err) {
      console.error('Failed to load clocked-in employees:', err);
    }
  };

  const handleSelectEmployee = (emp: Employee) => {
    setSelectedEmployee(emp);
    setSearchTerm(`${emp.first_name} ${emp.last_name}`);
    setShowEmployeeDropdown(false);

    // Try to pre-populate job based on crew foreman's location
    if (emp.crew_foreman_id) {
      // Find the foreman's current job from clocked-in data
      const foremanEntry = clockedInEmployees.find(
        ce => ce.employee_id === emp.crew_foreman_id
      );

      if (foremanEntry) {
        // Find the job in the jobs list
        const foremanJob = jobs.find(j => j.job_id === foremanEntry.job_id);
        if (foremanJob) {
          setSelectedJob(foremanJob);
          setJobSearchTerm(foremanJob.job_name);
        }
      }
    }
  };

  const handleSelectJob = (job: Job) => {
    setSelectedJob(job);
    setJobSearchTerm(job.job_name);
    setShowJobDropdown(false);
  };

  const filteredEmployees = employees.filter(emp =>
    `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredJobs = jobs.filter(job =>
    job.job_name.toLowerCase().includes(jobSearchTerm.toLowerCase())
  );

  // Filter clocked-in employees by selected job
  const jobClockedInEmployees = selectedJob
    ? clockedInEmployees.filter(ce => ce.job_id === selectedJob.job_id)
    : [];

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const checkIfClockedIn = async (employeeId: number) => {
    try {
      const response = await api.get(`${API_CONFIG.ENDPOINTS.CURRENT_TIME_ENTRY}?employee_id=${employeeId}`);
      return response.data.data; // Returns the open entry or null
    } catch (err) {
      console.error('Failed to check clock status:', err);
      return null;
    }
  };

  const handleClockIn = async () => {
    setError(null);
    setSuccess(null);

    if (!selectedEmployee) {
      setErrorMessage('Please select an employee');
      setShowErrorModal(true);
      return;
    }

    if (!selectedJob) {
      setErrorMessage('Please select a job');
      setShowErrorModal(true);
      return;
    }

    if (!date || !time) {
      setErrorMessage('Please select a date and time');
      setShowErrorModal(true);
      return;
    }

    // Check if already clocked in
    const openEntry = await checkIfClockedIn(selectedEmployee.employee_id);
    if (openEntry) {
      setErrorMessage(`${selectedEmployee.first_name} ${selectedEmployee.last_name} is already clocked in. Please clock them out first.`);
      setShowErrorModal(true);
      return;
    }

    setLoading(true);

    try {
      // Send date and time as separate string components with timezone offset
      // This prevents timezone conversion issues
      // The backend will handle converting local time to UTC properly
      const payload = {
        employee_id: selectedEmployee.employee_id,
        job_id: selectedJob.job_id,
        clock_in_date: date, // e.g., "2024-10-05"
        clock_in_time: time, // e.g., "14:00"
        timezone_offset_minutes: new Date().getTimezoneOffset(), // User's timezone offset
        clock_out: null,
        lunch_taken: false,
        lunch_minutes: 0,
        lat: 0,
        lng: 0
      };

      const response = await api.post(API_CONFIG.ENDPOINTS.CREATE_TIME_ENTRY, payload);

      if (response.data.success) {
        // Show success modal
        setSuccessMessage(`${selectedEmployee.first_name} ${selectedEmployee.last_name} clocked in successfully at ${time} on ${date}`);
        setShowSuccessModal(true);

        // Reload clocked-in employees
        await loadClockedInEmployees();

        // Reset form after modal closes
        setSelectedEmployee(null);
        setSelectedJob(null);
        setSearchTerm('');
        setJobSearchTerm('');
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        setDate(`${year}-${month}-${day}`);
        const hours = today.getHours().toString().padStart(2, '0');
        const minutes = today.getMinutes().toString().padStart(2, '0');
        setTime(`${hours}:${minutes}`);
      } else {
        setErrorMessage(response.data.message || 'Failed to clock in');
        setShowErrorModal(true);
      }
    } catch (err: any) {
      console.error('Failed to clock in:', err);
      setErrorMessage(err.response?.data?.message || 'Failed to clock in');
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    setError(null);
    setSuccess(null);

    if (!selectedEmployee) {
      setErrorMessage('Please select an employee');
      setShowErrorModal(true);
      return;
    }

    if (!date || !time) {
      setErrorMessage('Please select a date and time');
      setShowErrorModal(true);
      return;
    }

    setLoading(true);

    try {
      // Check if employee is clocked in
      const openEntry = await checkIfClockedIn(selectedEmployee.employee_id);

      if (!openEntry) {
        setErrorMessage(`${selectedEmployee.first_name} ${selectedEmployee.last_name} is not currently clocked in. Please clock them in first.`);
        setShowErrorModal(true);
        setLoading(false);
        return;
      }

      // Verify clock out is after clock in by comparing times
      const clockInDateTime = new Date(openEntry.clock_in);
      const clockOutDateTime = new Date(`${date}T${time}`);

      if (clockOutDateTime <= clockInDateTime) {
        setErrorMessage('Clock out time must be after clock in time');
        setShowErrorModal(true);
        setLoading(false);
        return;
      }

      // The backend editTimeEntry expects ISO strings in UTC
      // new Date() with a local datetime string (no 'Z') treats it as local time
      // and internally converts to UTC, so toISOString() gives correct UTC
      const clockOutTime = new Date(`${date}T${time}:00`);

      const editPayload = {
        entry_id: openEntry.entry_id,
        clock_in: openEntry.clock_in, // Keep existing clock in as-is
        clock_out: clockOutTime.toISOString(), // JS already converted local to UTC
        lunch_minutes: openEntry.lunch_minutes || 0,
        shop_time_minutes: 0,
        edit_reason: `Manual clock out by ${auth.user?.first_name} ${auth.user?.last_name}`
      };

      const response = await api.put(API_CONFIG.ENDPOINTS.EDIT_TIME, editPayload);

      if (response.data.success) {
        // Show success modal
        setSuccessMessage(`${selectedEmployee.first_name} ${selectedEmployee.last_name} clocked out successfully at ${time} on ${date}`);
        setShowSuccessModal(true);

        // Reload clocked-in employees
        await loadClockedInEmployees();

        // Reset form after modal closes
        setSelectedEmployee(null);
        setSelectedJob(null);
        setSearchTerm('');
        setJobSearchTerm('');
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        setDate(`${year}-${month}-${day}`);
        const hours = today.getHours().toString().padStart(2, '0');
        const minutes = today.getMinutes().toString().padStart(2, '0');
        setTime(`${hours}:${minutes}`);
      } else {
        setErrorMessage(response.data.message || 'Failed to clock out');
        setShowErrorModal(true);
      }
    } catch (err: any) {
      console.error('Failed to clock out:', err);
      setErrorMessage(err.response?.data?.message || 'Failed to clock out');
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '800px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      backgroundColor: '#f8fafc',
      minHeight: '100vh'
    },
    header: {
      marginBottom: '32px',
      backgroundColor: 'white',
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    subtitle: {
      color: '#6b7280',
      fontSize: '16px'
    },
    card: {
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '32px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      marginBottom: '24px'
    },
    formGroup: {
      marginBottom: '24px'
    },
    label: {
      display: 'block',
      fontSize: '16px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '10px'
    },
    dropdownContainer: {
      position: 'relative' as const
    },
    dropdownInput: {
      width: '100%',
      padding: '14px 40px 14px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '500',
      backgroundColor: 'white',
      cursor: 'pointer',
      transition: 'all 0.2s',
      color: '#1f2937'
    },
    dropdownInputFocused: {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
    },
    dropdownIcon: {
      position: 'absolute' as const,
      right: '16px',
      top: '50%',
      transform: 'translateY(-50%)',
      cursor: 'pointer',
      color: '#6b7280'
    },
    dropdownList: {
      position: 'absolute' as const,
      top: 'calc(100% + 8px)',
      left: 0,
      right: 0,
      backgroundColor: 'white',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      maxHeight: '300px',
      overflowY: 'auto' as const,
      zIndex: 100,
      boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
    },
    dropdownItem: {
      padding: '14px 16px',
      cursor: 'pointer',
      borderBottom: '1px solid #f3f4f6',
      transition: 'all 0.15s',
      fontSize: '16px',
      fontWeight: '500',
      color: '#374151'
    },
    dropdownItemSelected: {
      backgroundColor: '#dbeafe',
      color: '#1d4ed8',
      fontWeight: '600'
    },
    input: {
      width: '100%',
      padding: '14px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '500',
      color: '#1f2937',
      transition: 'all 0.2s'
    },
    buttonContainer: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '16px',
      marginTop: '32px'
    },
    clockInButton: {
      padding: '18px 24px',
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '18px',
      fontWeight: '700',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.2s',
      boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
    },
    clockOutButton: {
      padding: '18px 24px',
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '18px',
      fontWeight: '700',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.2s',
      boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)'
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    error: {
      backgroundColor: '#fef2f2',
      border: '2px solid #fecaca',
      color: '#dc2626',
      padding: '16px',
      borderRadius: '8px',
      marginBottom: '20px',
      fontSize: '16px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    success: {
      backgroundColor: '#f0fdf4',
      border: '2px solid #bbf7d0',
      color: '#16a34a',
      padding: '16px',
      borderRadius: '8px',
      marginBottom: '20px',
      fontSize: '16px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    clockedInSection: {
      marginTop: '24px',
      padding: '20px',
      backgroundColor: '#f0f9ff',
      border: '2px solid #bfdbfe',
      borderRadius: '8px'
    },
    clockedInTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1e40af',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    clockedInList: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '8px'
    },
    clockedInItem: {
      padding: '12px',
      backgroundColor: 'white',
      borderRadius: '6px',
      fontSize: '15px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      border: '1px solid #e0e7ff'
    },
    clockedInName: {
      fontWeight: '500',
      color: '#374151'
    },
    clockedInTime: {
      color: '#6b7280',
      fontSize: '14px',
      fontFamily: 'monospace'
    },
    noEmployees: {
      padding: '12px',
      color: '#6b7280',
      fontStyle: 'italic',
      fontSize: '14px',
      textAlign: 'center' as const
    },
    successModal: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    },
    successModalContent: {
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '40px 32px',
      maxWidth: '500px',
      width: '100%',
      textAlign: 'center' as const,
      boxShadow: '0 20px 50px rgba(0,0,0,0.3)'
    },
    successIcon: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      backgroundColor: '#d1fae5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 24px',
      color: '#059669'
    },
    successTitle: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '12px'
    },
    successText: {
      fontSize: '16px',
      color: '#6b7280',
      marginBottom: '32px',
      lineHeight: '1.5'
    },
    successButton: {
      padding: '14px 32px',
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '600',
      transition: 'all 0.2s',
      boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
    },
    errorModal: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    },
    errorModalContent: {
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '40px 32px',
      maxWidth: '500px',
      width: '100%',
      textAlign: 'center' as const,
      boxShadow: '0 20px 50px rgba(0,0,0,0.3)'
    },
    errorIcon: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      backgroundColor: '#fee2e2',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 24px',
      color: '#dc2626'
    },
    errorTitle: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '12px'
    },
    errorText: {
      fontSize: '16px',
      color: '#6b7280',
      marginBottom: '32px',
      lineHeight: '1.5'
    },
    errorButton: {
      padding: '14px 32px',
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '600',
      transition: 'all 0.2s',
      boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          <Clock size={32} />
          Forgot Clock In/Out
        </h1>
        <p style={styles.subtitle}>
          Manually clock in or out employees who forgot to use the time clock
        </p>
      </div>

      <div style={styles.card}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Employee *</label>
          <div style={styles.dropdownContainer}>
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowEmployeeDropdown(true);
              }}
              onFocus={() => setShowEmployeeDropdown(true)}
              style={{
                ...styles.dropdownInput,
                ...(showEmployeeDropdown ? styles.dropdownInputFocused : {})
              }}
            />
            <ChevronDown
              size={20}
              style={styles.dropdownIcon}
              onClick={() => {
                setSearchTerm('');
                setShowEmployeeDropdown(true);
              }}
            />

            {showEmployeeDropdown && filteredEmployees.length > 0 && (
              <div style={styles.dropdownList}>
                {filteredEmployees.map(emp => (
                  <div
                    key={emp.employee_id}
                    style={{
                      ...styles.dropdownItem,
                      ...(selectedEmployee?.employee_id === emp.employee_id ? styles.dropdownItemSelected : {})
                    }}
                    onClick={() => handleSelectEmployee(emp)}
                    onMouseOver={(e) => {
                      if (selectedEmployee?.employee_id !== emp.employee_id) {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (selectedEmployee?.employee_id !== emp.employee_id) {
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    {emp.first_name} {emp.last_name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Job Location (required for clock in only)</label>
          <div style={styles.dropdownContainer}>
            <input
              type="text"
              placeholder="Search jobs..."
              value={jobSearchTerm}
              onChange={(e) => {
                setJobSearchTerm(e.target.value);
                setShowJobDropdown(true);
              }}
              onFocus={() => setShowJobDropdown(true)}
              style={{
                ...styles.dropdownInput,
                ...(showJobDropdown ? styles.dropdownInputFocused : {})
              }}
            />
            <ChevronDown
              size={20}
              style={styles.dropdownIcon}
              onClick={() => {
                setJobSearchTerm('');
                setShowJobDropdown(true);
              }}
            />

            {showJobDropdown && filteredJobs.length > 0 && (
              <div style={styles.dropdownList}>
                {filteredJobs.map(job => (
                  <div
                    key={job.job_id}
                    style={{
                      ...styles.dropdownItem,
                      ...(selectedJob?.job_id === job.job_id ? styles.dropdownItemSelected : {})
                    }}
                    onClick={() => handleSelectJob(job)}
                    onMouseOver={(e) => {
                      if (selectedJob?.job_id !== job.job_id) {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (selectedJob?.job_id !== job.job_id) {
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    {job.job_name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {selectedJob && jobClockedInEmployees.length > 0 && (
          <div style={styles.clockedInSection}>
            <div style={styles.clockedInTitle}>
              <Users size={18} />
              Employees Currently Clocked In at {selectedJob.job_name}
            </div>
            <div style={styles.clockedInList}>
              {jobClockedInEmployees.map(emp => (
                <div key={emp.employee_id} style={styles.clockedInItem}>
                  <span style={styles.clockedInName}>
                    {emp.first_name} {emp.last_name}
                  </span>
                  <span style={styles.clockedInTime}>
                    Clocked in at {formatTime(emp.clock_in)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedJob && jobClockedInEmployees.length === 0 && (
          <div style={styles.clockedInSection}>
            <div style={styles.clockedInTitle}>
              <Users size={18} />
              Employees at {selectedJob.job_name}
            </div>
            <div style={styles.noEmployees}>
              No employees currently clocked in at this job
            </div>
          </div>
        )}

        <div style={styles.formGroup}>
          <label style={styles.label}>Date *</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={styles.input}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Time *</label>
          <CustomTimePicker
            value={time}
            onChange={(val) => setTime(val)}
            label="Select Time"
          />
        </div>

        <div style={styles.buttonContainer}>
          <button
            style={{
              ...styles.clockInButton,
              ...(loading ? styles.buttonDisabled : {})
            }}
            onClick={handleClockIn}
            disabled={loading}
            onMouseOver={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = '#059669';
              }
            }}
            onMouseOut={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = '#10b981';
              }
            }}
          >
            <LogIn size={22} />
            Clock In
          </button>
          <button
            style={{
              ...styles.clockOutButton,
              ...(loading ? styles.buttonDisabled : {})
            }}
            onClick={handleClockOut}
            disabled={loading}
            onMouseOver={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = '#dc2626';
              }
            }}
            onMouseOut={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = '#ef4444';
              }
            }}
          >
            <LogOut size={22} />
            Clock Out
          </button>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div style={styles.successModal} onClick={() => setShowSuccessModal(false)}>
          <div style={styles.successModalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.successIcon}>
              <CheckCircle size={48} strokeWidth={2.5} />
            </div>
            <h2 style={styles.successTitle}>Success!</h2>
            <p style={styles.successText}>{successMessage}</p>
            <button
              style={styles.successButton}
              onClick={() => setShowSuccessModal(false)}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#059669';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#10b981';
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div style={styles.errorModal} onClick={() => setShowErrorModal(false)}>
          <div style={styles.errorModalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.errorIcon}>
              <AlertCircle size={48} strokeWidth={2.5} />
            </div>
            <h2 style={styles.errorTitle}>Error</h2>
            <p style={styles.errorText}>{errorMessage}</p>
            <button
              style={styles.errorButton}
              onClick={() => setShowErrorModal(false)}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#dc2626';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#ef4444';
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
